import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { searchEntities, refUrl } from "@/lib/api";

const TYPE_BADGE = {
  service: { label: "Service", cls: "bg-[#e7f0f8] text-[#16385f]" },
  company: { label: "Platform", cls: "bg-[#eaf4ec] text-[#0b6b3a]" },
  category: { label: "Category", cls: "bg-[#fdf0e3] text-[#8a4b0f]" },
};

export default function SearchBar({ variant = "header" }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const data = await searchEntities(q.trim(), 8);
        setResults(data.results || []);
        setOpen(true);
        setActive(-1);
      } catch (e) {
        setResults([]);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (item) => {
    setOpen(false);
    setQ("");
    navigate(refUrl(item));
  };

  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && results[active]) return go(results[active]);
    if (q.trim()) {
      setOpen(false);
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    }
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    else if (e.key === "Escape") setOpen(false);
  };

  const big = variant === "hero";

  return (
    <div ref={boxRef} className="relative w-full" data-testid="global-search">
      <form onSubmit={submit} className="relative">
        <Search
          size={big ? 20 : 16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
        />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onKeyDown={onKey}
          placeholder={big ? "Search services, platforms, categories…" : "Search knowledge base…"}
          data-testid="search-input"
          className={`w-full rounded-[4px] border border-[var(--line)] bg-white outline-none focus:border-[var(--gov-blue)] focus:ring-2 focus:ring-[var(--gov-blue)]/20 ${
            big ? "py-3.5 pl-11 pr-10 text-base" : "py-2 pl-9 pr-8 text-sm"
          }`}
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(""); setResults([]); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)]"
            data-testid="search-clear"
            aria-label="Clear"
          >
            <X size={big ? 18 : 15} />
          </button>
        )}
      </form>

      {open && results.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full overflow-hidden rounded-[4px] border border-[var(--line)] bg-white shadow-xl"
          data-testid="search-dropdown"
        >
          {results.map((r, i) => {
            const b = TYPE_BADGE[r.type] || {};
            return (
              <button
                key={`${r.type}-${r.slug}`}
                onClick={() => go(r)}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-start gap-2 border-b border-[var(--line-soft)] px-3 py-2.5 text-left last:border-0 ${
                  active === i ? "bg-[var(--bg-panel)]" : "bg-white"
                }`}
                data-testid={`search-result-${r.type}-${r.slug}`}
              >
                <span className={`mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${b.cls}`}>
                  {b.label}
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-[var(--ink)]">{r.name}</span>
                  {r.match === "content" && (
                    <span className="text-[11px] text-[var(--muted)]">matched in page content</span>
                  )}
                </span>
              </button>
            );
          })}
          <button
            onClick={submit}
            className="block w-full bg-[var(--bg-alt)] px-3 py-2 text-center text-xs font-semibold text-[var(--gov-blue)] hover:underline"
            data-testid="search-see-all"
          >
            See all results for “{q}”
          </button>
        </div>
      )}
    </div>
  );
}
